/**
 * Campus Passport - Student profile with badges and stamp collection
 * Premium dark mode design with glass-morphism effects
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useShareMilestone } from '@/components/sharing';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PeopleYouMayKnow } from '@/components/social/PeopleYouMayKnow';
import { ReputationBadge } from '@/components/reputation/ReputationBadge';
import {
  Award,
  Calendar,
  MapPin,
  Lock,
  Unlock,
  Edit2,
  Camera,
  Trophy,
  Star,
  Sparkles,
  ChevronRight,
  TrendingUp,
  Share2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Glass panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={`
      bg-white/[0.03] backdrop-blur-xl 
      border border-white/[0.06] 
      rounded-2xl
      ${className}
    `}
    {...props}
  >
    {children}
  </div>
);

// Badge card component with glow effect
const BadgeCard = ({ badge, earned = false, onShare }) => {
  const glowColor = earned ? 'rgba(247, 200, 115, 0.3)' : 'transparent';
  
  return (
    <div
      className={`
        relative p-4 rounded-xl border transition-all duration-300 group
        ${earned 
          ? 'bg-soft-amber/10 border-soft-amber/30 hover:border-soft-amber/50' 
          : 'bg-white/[0.02] border-white/[0.06] opacity-50'
        }
      `}
      style={{ boxShadow: earned ? `0 0 20px ${glowColor}` : 'none' }}
    >
      <div className="flex items-start gap-3">
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center
          ${earned ? 'bg-soft-amber/20' : 'bg-white/[0.05]'}
        `}>
          <Award className={`w-6 h-6 ${earned ? 'text-soft-amber' : 'text-muted-text'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-medium ${earned ? 'text-cloud-white' : 'text-soft-silver'}`}>
            {badge.badge_title || badge.title}
          </h4>
          <p className="text-xs text-muted-text mt-0.5 line-clamp-2">
            {badge.badge_description || badge.description}
          </p>
          {earned && badge.awarded_at && (
            <p className="text-xs text-soft-amber/70 mt-2 font-mono">
              Earned {new Date(badge.awarded_at).toLocaleDateString()}
            </p>
          )}
        </div>
        {earned ? (
          <button 
            onClick={() => onShare?.(badge)}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-soft-amber/20"
            title="Share badge"
          >
            <Share2 className="w-4 h-4 text-soft-amber" />
          </button>
        ) : (
          <Sparkles className="w-4 h-4 text-muted-text opacity-30" />
        )}
      </div>
    </div>
  );
};

// Stamp card component
const StampCard = ({ stamp }) => (
  <div className="group relative p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-electric-ice-blue/30 transition-all duration-300">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
        <Calendar className="w-5 h-5 text-electric-ice-blue" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-cloud-white truncate">
          {stamp.stamp_title || 'Event Check-In'}
        </h4>
        <div className="flex items-center gap-2 mt-1">
          <MapPin className="w-3 h-3 text-muted-text" />
          <span className="text-xs text-muted-text truncate">
            {stamp.organization_name || 'Campus Event'}
          </span>
        </div>
      </div>
      <span className="text-xs text-muted-text font-mono">
        {new Date(stamp.stamped_at).toLocaleDateString()}
      </span>
    </div>
    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-electric-ice-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
  </div>
);

// Stat card component
const StatCard = ({ label, value, icon: Icon, trend }) => (
  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
    <div className="flex items-center justify-between mb-2">
      <Icon className="w-4 h-4 text-muted-text" />
      {trend && (
        <div className="flex items-center gap-1 text-xs text-cool-mint">
          <TrendingUp className="w-3 h-3" />
          {trend}
        </div>
      )}
    </div>
    <p className="text-2xl font-mono font-semibold text-cloud-white">{value}</p>
    <p className="text-xs text-muted-text mt-1">{label}</p>
  </div>
);

export const Passport = () => {
  const { user, session } = useAuth();
  const { shareBadge, sharePassportMilestone, shareEventsMilestone } = useShareMilestone();
  const [loading, setLoading] = useState(true);
  const [passport, setPassport] = useState(null);
  const [badgeDefinitions, setBadgeDefinitions] = useState({});
  const [error, setError] = useState(null);

  // Fetch passport data
  const fetchPassport = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const [passportRes, badgesRes] = await Promise.all([
        fetch(`${API_URL}/api/passport/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
        fetch(`${API_URL}/api/passport/badges/definitions`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }),
      ]);

      if (passportRes.ok) {
        const data = await passportRes.json();
        setPassport(data);
      }

      if (badgesRes.ok) {
        const definitions = await badgesRes.json();
        setBadgeDefinitions(definitions);
      }
    } catch (err) {
      console.error('Failed to fetch passport:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchPassport();
  }, [fetchPassport]);

  // Toggle privacy
  const togglePrivacy = async () => {
    if (!passport?.profile) return;
    
    try {
      const res = await fetch(`${API_URL}/api/passport/me`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_public: !passport.profile.is_public }),
      });
      
      if (res.ok) {
        const updated = await res.json();
        setPassport(updated);
      }
    } catch (err) {
      console.error('Failed to update privacy:', err);
    }
  };

  // Share handlers - must be defined before early returns
  const handleShareBadge = useCallback((badge) => {
    shareBadge({
      name: badge.badge_title || badge.title,
      description: badge.badge_description || badge.description,
      tier: badge.tier || 'gold',
      earned_at: badge.awarded_at,
    });
  }, [shareBadge]);

  const handleSharePassportStats = useCallback((passportStats) => {
    shareEventsMilestone({
      count: passportStats.total_events,
      check_ins: passportStats.total_checkins,
    });
  }, [shareEventsMilestone]);

  const handleShareBadgesMilestone = useCallback((passportStats) => {
    sharePassportMilestone({
      type: 'badges',
      value: passportStats.total_badges,
      name: 'Badges Earned',
    });
  }, [sharePassportMilestone]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-electric-ice-blue border-t-transparent rounded-full animate-spin" />
          <p className="text-soft-silver text-sm">Loading passport...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <GlassPanel className="p-8 text-center max-w-md">
          <Award className="w-12 h-12 text-muted-text mx-auto mb-4" />
          <h2 className="text-lg font-medium text-cloud-white mb-2">Unable to Load Passport</h2>
          <p className="text-sm text-muted-text mb-4">{error}</p>
          <Button onClick={fetchPassport} variant="outline" size="sm">
            Try Again
          </Button>
        </GlassPanel>
      </div>
    );
  }

  const profile = passport?.profile || {};
  const badges = passport?.badges || [];
  const stamps = passport?.recent_stamps || [];
  const stats = passport?.stats || { total_events: 0, total_checkins: 0, total_badges: 0 };

  // Get all badge definitions and mark earned ones
  const allBadges = Object.entries(badgeDefinitions).map(([code, def]) => ({
    code,
    ...def,
    earned: badges.some(b => b.badge === code),
    awardedAt: badges.find(b => b.badge === code)?.awarded_at,
  }));

  return (
    <div data-testid="passport-page" className="space-y-6 pb-8">
      {/* Header Section */}
      <div className="relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-electric-ice-blue/5 to-transparent rounded-3xl" />
        
        <GlassPanel className="relative p-6 md:p-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gradient-to-br from-electric-ice-blue/20 to-cool-mint/20 flex items-center justify-center border border-white/[0.1]">
                {profile.profile_image_url ? (
                  <img 
                    src={profile.profile_image_url} 
                    alt="Profile" 
                    className="w-full h-full rounded-2xl object-cover"
                  />
                ) : (
                  <span className="text-3xl md:text-4xl font-semibold text-cloud-white">
                    {(profile.display_name || user?.email)?.[0]?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <button className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-graphite border border-white/[0.1] flex items-center justify-center hover:bg-white/[0.05] transition-colors">
                <Camera className="w-4 h-4 text-soft-silver" />
              </button>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-semibold text-cloud-white">
                    {profile.display_name || 'Campus Explorer'}
                  </h1>
                  <p className="text-soft-silver mt-1">{user?.email}</p>
                  {profile.bio && (
                    <p className="text-muted-text mt-2 text-sm max-w-md">{profile.bio}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={togglePrivacy}
                    className="text-muted-text hover:text-cloud-white"
                  >
                    {profile.is_public ? (
                      <><Unlock className="w-4 h-4 mr-2" /> Public</>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" /> Private</>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" className="border-white/[0.1]">
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </Button>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <StatCard label="Events Attended" value={stats.total_events} icon={Calendar} trend="+5" />
                <StatCard label="Check-Ins" value={stats.total_checkins} icon={MapPin} />
                <StatCard label="Badges Earned" value={stats.total_badges} icon={Award} />
              </div>
              
              {/* Share Button */}
              {stats.total_events > 0 && (
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleSharePassportStats(stats)}
                    size="sm"
                    className="bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 border border-violet-500/30"
                    data-testid="share-passport-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Events Milestone
                  </Button>
                  {stats.total_badges > 0 && (
                    <Button
                      onClick={() => handleShareBadgesMilestone(stats)}
                      size="sm"
                      variant="ghost"
                      className="text-soft-amber hover:bg-soft-amber/10"
                      data-testid="share-badges-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Badges
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </GlassPanel>
      </div>

      {/* Badges Section */}
      <section data-testid="badges-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-cloud-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-soft-amber" />
            Badges
          </h2>
          <span className="text-sm text-muted-text">
            {badges.length} / {allBadges.length} earned
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allBadges.map((badge) => (
            <BadgeCard 
              key={badge.code} 
              badge={{
                ...badge,
                badge_title: badge.title,
                badge_description: badge.description,
                awarded_at: badge.awardedAt,
              }} 
              earned={badge.earned}
              onShare={handleShareBadge}
            />
          ))}
        </div>
      </section>

      {/* Recent Stamps */}
      <section data-testid="stamps-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-cloud-white flex items-center gap-2">
            <Star className="w-5 h-5 text-electric-ice-blue" />
            Recent Stamps
          </h2>
          <Button variant="ghost" size="sm" className="text-muted-text hover:text-electric-ice-blue">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {stamps.length > 0 ? (
          <div className="space-y-3">
            {stamps.map((stamp) => (
              <StampCard key={stamp.id} stamp={stamp} />
            ))}
          </div>
        ) : (
          <GlassPanel className="p-8 text-center">
            <Calendar className="w-12 h-12 text-muted-text mx-auto mb-4" />
            <h3 className="text-cloud-white font-medium mb-2">No Stamps Yet</h3>
            <p className="text-sm text-muted-text">
              Attend events and check in to collect stamps on your passport!
            </p>
          </GlassPanel>
        )}
      </section>

      {/* Social & Reputation Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Reputation Score */}
        <section data-testid="reputation-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-cloud-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
              Your Reputation
            </h2>
          </div>
          <GlassPanel className="p-6">
            <ReputationBadge
              userId={user?.id}
              type="student"
              session={session}
              variant="full"
            />
          </GlassPanel>
        </section>

        {/* People You May Know */}
        <section data-testid="people-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-cloud-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              Connect
            </h2>
          </div>
          <GlassPanel className="p-6">
            <PeopleYouMayKnow
              userId={user?.id}
              session={session}
              limit={3}
              showHeader={false}
            />
          </GlassPanel>
        </section>
      </div>
    </div>
  );
};

export default Passport;
